'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { X, Menu, LogOut, LogIn, ChevronRight, ChevronDown } from 'lucide-react'
import { Button } from './Button'
import { ButtonVariant, ButtonStyle } from './Button'
import { Body } from './Typography'
import type { LucideIcon } from 'lucide-react'
import { isNewTabClick } from '../utils/newTabClick'

export interface SidebarMenuItem {
  id: string
  label: string
  icon?: LucideIcon
  path?: string
  children?: SidebarMenuItem[]
}

export interface SidebarBranding {
  logoText: string
  appName: string
  appSubtitle?: string
  logoIcon?: string
  logoComponent?: React.ReactNode
}

export interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  showCloseButton?: boolean
  branding: SidebarBranding
  menuItems: SidebarMenuItem[]
  currentPath: string
  onNavigate: (path: string) => void
  
  // Auth related
  isAuthenticated?: boolean
  onLogin?: () => void
  onLogout?: () => void
  
  // Optional footer content
  footer?: React.ReactNode
  
  // Desktop collapse functionality
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function Sidebar({ 
  isOpen, 
  onToggle, 
  showCloseButton = true,
  branding,
  menuItems,
  currentPath,
  onNavigate,
  isAuthenticated = false,
  onLogin,
  onLogout,
  footer,
  isCollapsed = false,
  onToggleCollapse
}: SidebarProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(findActiveAncestorIds(menuItems, currentPath))
  )

  const isItemActive = (item: SidebarMenuItem): boolean => {
    if (item.path && currentPath === item.path) {
      return true
    }

    return item.children?.some(isItemActive) ?? false
  }

  useEffect(() => {
    const activeAncestors = findActiveAncestorIds(menuItems, currentPath)
    if (activeAncestors.length === 0) return

    setExpandedIds(prev => {
      const next = new Set(prev)
      for (const id of activeAncestors) {
        next.add(id)
      }
      return next
    })
  }, [menuItems, currentPath])

  const handleNavigation = (path: string) => {
    onNavigate(path)
    // Close sidebar on mobile after navigation
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      onToggle()
    }
  }

  const handleAuth = () => {
    if (isAuthenticated && onLogout) {
      onLogout()
    } else if (!isAuthenticated && onLogin) {
      onLogin()
    }
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div 
        className="h-full bg-background-white border-r border-gray-200/20 shadow-lg md:shadow-none md:relative md:h-screen fixed left-0 top-0 z-50 md:z-auto transition-all duration-300"
        style={{ 
          width: isCollapsed ? '80px' : '280px',
          transform: showCloseButton ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
          transition: showCloseButton ? 'transform 0.3s ease' : 'all 0.3s ease'
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`border-b border-gray-200/20 ${isCollapsed ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'space-x-3'}`}>
                {branding.logoComponent ? (
                  branding.logoComponent
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">
                      {branding.logoIcon || branding.logoText.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {!isCollapsed && (
                  <div>
                    <div className="font-bold text-text-primary">{branding.appName}</div>
                    {branding.appSubtitle && (
                      <Body className="text-text-tertiary text-xs">{branding.appSubtitle}</Body>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop collapse toggle */}
              {!isCollapsed && onToggleCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className="hidden md:block p-1 hover:bg-background-secondary rounded transition-colors"
                  title="Recolher sidebar"
                >
                  <Menu className="w-4 h-4 text-text-secondary" />
                </button>
              )}

              {/* Close button for mobile */}
              {showCloseButton && (
                <button
                  onClick={onToggle}
                  className="md:hidden p-2 hover:bg-background-secondary rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-text-secondary" />
                </button>
              )}
            </div>

            {/* Expand button for collapsed state */}
            {isCollapsed && onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="hidden md:block w-full mt-3 p-2 hover:bg-background-secondary rounded-lg transition-colors"
                title="Expandir sidebar"
              >
                <Menu className="w-4 h-4 text-text-secondary mx-auto" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 min-h-0 overflow-y-auto space-y-2 ${isCollapsed ? 'p-2' : 'p-4'}`}>
            {renderMenuItems(menuItems, {
              isCollapsed,
              isItemActive,
              expandedIds,
              onToggleExpand: (id) => {
                setExpandedIds(prev => {
                  const next = new Set(prev)
                  if (next.has(id)) {
                    next.delete(id)
                  } else {
                    next.add(id)
                  }
                  return next
                })
              },
              handleNavigation,
              currentPath
            })}
          </nav>

          {/* Footer */}
          <div className={`border-t border-gray-200/20 ${isCollapsed ? 'p-2' : 'p-4'}`}>
            {footer ? (
              footer
            ) : (
              /* Default auth button */
              (onLogin || onLogout) && (
                <Button
                  variant={isAuthenticated ? ButtonVariant.NEGATIVE : ButtonVariant.PRIMARY}
                  buttonStyle={isAuthenticated ? ButtonStyle.CLEAR : ButtonStyle.FILLED}
                  onClick={handleAuth}
                  className={`w-full flex items-center justify-center ${isCollapsed ? 'p-3' : 'gap-2'}`}
                  title={isCollapsed ? (isAuthenticated ? 'Sair' : 'Entrar') : undefined}
                >
                  {isAuthenticated ? (
                    <>
                      <LogOut className="w-4 h-4" />
                      {!isCollapsed && 'Sair'}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      {!isCollapsed && 'Entrar'}
                    </>
                  )}
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </>
  )
}

type RenderOptions = {
  isCollapsed: boolean
  isItemActive: (item: SidebarMenuItem) => boolean
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  handleNavigation: (path: string) => void
  currentPath: string
}

function renderMenuItems(items: SidebarMenuItem[], options: RenderOptions, level = 0): React.ReactNode {
  return items.map((item) => {
    const isCurrentPage = Boolean(item.path && options.currentPath === item.path)
    const Icon = item.icon
    const hasChildren = Boolean(item.children?.length)
    const isLeaf = !hasChildren
    const canNavigate = Boolean(item.path)
    const isExpanded = options.expandedIds.has(item.id)
    // Groups toggle their children when expanded; everything else with a path is a real
    // link so the browser can open it in a new tab (cmd/ctrl/middle-click, context menu).
    const togglesChildren = hasChildren && !options.isCollapsed
    const rendersAsLink = canNavigate && !togglesChildren
    const Row: 'a' | 'button' = rendersAsLink ? 'a' : 'button'

    const row = (
      <Row
        href={rendersAsLink ? item.path : undefined}
        type={rendersAsLink ? undefined : 'button'}
        onClick={(event) => {
          if (togglesChildren) {
            options.onToggleExpand(item.id)
            return
          }

          if (!canNavigate || !item.path) {
            return
          }

          if (isNewTabClick(event)) {
            // Let the browser open the anchor natively.
            return
          }

          event.preventDefault()
          options.handleNavigation(item.path)
        }}
        className={`
          w-full max-w-full flex items-center rounded-xl transition-all duration-200 text-left group relative overflow-hidden
          ${options.isCollapsed ? 'justify-center p-2.5' : 'gap-2.5 py-2'}
          ${isCurrentPage
            ? 'bg-brand-primary/10 text-brand-primary border border-brand-primary/25'
            : canNavigate
              ? 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/80 border border-transparent'
              : hasChildren
                ? 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/80 border border-transparent'
                : 'text-text-tertiary border border-transparent'}
          ${!canNavigate && !hasChildren ? 'cursor-default' : ''}
        `}
        style={
          !options.isCollapsed
            ? {
                paddingLeft: `${12 + level * 12}px`,
                paddingRight: '10px'
              }
            : undefined
        }
        title={options.isCollapsed ? item.label : undefined}
      >
        {Icon ? (
          <Icon className={`w-4 h-4 ${isCurrentPage ? 'text-brand-primary' : 'text-current'} ${options.isCollapsed ? 'flex-shrink-0' : ''}`} />
        ) : (
          <div className={`w-2 h-2 rounded-full ${isCurrentPage ? 'bg-brand-primary' : 'bg-gray-300'} ${options.isCollapsed ? '' : 'ml-1 mr-2'}`} />
        )}

        {!options.isCollapsed && (
          <>
            <span className={`min-w-0 truncate ${isLeaf ? 'text-sm font-medium' : 'uppercase text-[11px] tracking-wide font-semibold'}`}>
              {item.label}
            </span>
            {hasChildren && (
              isExpanded
                ? <ChevronDown className="ml-auto w-3.5 h-3.5 text-text-secondary" />
                : <ChevronRight className="ml-auto w-3.5 h-3.5 text-text-secondary" />
            )}
            {isCurrentPage && canNavigate && (
              <motion.div
                layoutId="activeIndicator"
                className={`${hasChildren ? '' : 'ml-auto'} w-2 h-2 bg-brand-primary rounded-full`}
              />
            )}
          </>
        )}

        {options.isCollapsed && (
          <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 whitespace-nowrap">
            {item.label}
            <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-0 h-0 border-r-4 border-r-gray-900 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
          </div>
        )}

        {options.isCollapsed && isCurrentPage && canNavigate && (
          <motion.div
            layoutId="activeIndicatorCollapsed"
            className="absolute right-1 w-1 h-6 bg-brand-primary rounded-full"
          />
        )}
      </Row>
    )

    if (options.isCollapsed || !hasChildren) {
      return (
        <div key={item.id} className="w-full">
          {row}
        </div>
      )
    }

    return (
      <div key={item.id} className="w-full">
        {row}
        {isExpanded && (
          <div className="mt-0.5 space-y-0.5 rounded-lg border-l border-border-primary/40 pl-1">
            {renderMenuItems(item.children ?? [], options, level + 1)}
          </div>
        )}
      </div>
    )
  })
}

function findActiveAncestorIds(items: SidebarMenuItem[], currentPath: string, acc: string[] = []): string[] {
  for (const item of items) {
    const nextAcc = item.children?.length ? [...acc, item.id] : acc

    if (item.path === currentPath) {
      return acc
    }

    if (item.children?.length) {
      const fromChild = findActiveAncestorIds(item.children, currentPath, nextAcc)
      if (fromChild.length > 0) {
        return fromChild
      }
    }
  }
  return []
}

// Mobile menu button component
export function MobileMenuButton({ onToggle }: { onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="md:hidden p-2 hover:bg-background-secondary rounded-lg transition-colors"
    >
      <Menu className="w-6 h-6 text-text-primary" />
    </button>
  )
}
